import { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import type { GridCellChange } from "../../../hooks/useForecastWorkspace/useWorkspaceMutationQueue";
import { createRowId } from "../utils/rowId";

type GridRowId = GridCellChange["data"];

/** A grid row: its identity, plus a value per column key. AG Grid row `data` satisfies it. */
export interface CopyRow extends GridRowId {
  /** Cell values, keyed by column (`W07`, `M03`, `PTG`, …). */
  [colId: string]: unknown;
}

/** The row-node slice the plan reads. AG Grid's `IRowNode` satisfies it. */
interface CopyRowNode {
  /** The row behind the node. */
  data?: unknown;
}

/**
 * The column slice the plan reads. Method syntax, not an arrow property, so AG
 * Grid's own `Column` stays assignable under strict parameter variance.
 */
interface CopyColumn {
  /** Whether the grid would let this row's cell in this column be edited. */
  isCellEditable(rowNode: CopyRowNode): boolean;
}

/** The `GridApi` slice the plan reads. */
export interface CopyGridApi {
  /** The row with this id, from `getRowId`'s scheme. */
  getRowNode(id: string): CopyRowNode | undefined;
  /** The column with this id. */
  getColumn(colId: string): CopyColumn | null;
}

/** Why a scope has no producer whose values a copy could take. */
export type CopySourceProblem = "noSource" | "manyProducers";

/** Inputs for {@link resolveCopySource}. */
export interface CopySourceInput {
  /** The rows in scope, source and own alike. */
  rows: readonly CopyRow[];
  /** The planner's own producer, or undefined on a frozen cube. */
  editableProducer: string | undefined;
}

/** Why a cell inside a copied row was not written. */
export type CopyCellSkipReason = "noSourceValue" | "notEditable";

/** One cell a copy writes: the queue's own shape, with a finite value. */
interface CopyCellChange extends Omit<GridCellChange, "value" | "oldValue"> {
  /** The source's value. */
  value: number;
  /**
   * What the planner had there, or 0 where they had nothing. An aggregate
   * recompute moves the weeks by `value − oldValue` (a ratio scales them by
   * `value / oldValue`), so a blank target reads 0 and the value spreads as
   * an edit onto a blank aggregate does.
   */
  oldValue: number;
}

/**
 * Why a row in scope was not copied at all. An own row is the target, not a
 * source; a read-only KPI and a KPI the planner's version lacks are decided
 * before any cell is looked at.
 */
export type CopyRowReason =
  | CopyCellSkipReason
  | "metricNotEditable"
  | "noTargetRow"
  | "ownRow";

/** A count per reason. Absent reasons cost nothing. */
type Tally<Reason extends string> = Partial<Record<Reason, number>>;

/**
 * What a copy would do: the cells it writes, and what it leaves alone — cells
 * inside a row it does write, and rows it writes nothing of — each by reason.
 */
export interface CopyPlan {
  /** The cells to write, in scope order. */
  changes: CopyCellChange[];
  /** Cells left unwritten in rows the copy does write. */
  skipped: Tally<CopyCellSkipReason>;
  /** Rows the copy writes nothing of. */
  rowsNotCopied: Tally<CopyRowReason>;
}

/** Inputs for {@link buildCopyPlan}. */
export interface CopyPlanInput {
  /** The rows in scope, source and own alike. */
  rows: readonly CopyRow[];
  /** The columns in scope: every unpinned column, hidden included. */
  colIds: readonly string[];
  /** The producer being copied from, per {@link resolveCopySource}. */
  sourceProducer: string;
  /** The planner's own producer, whose rows receive the copy. */
  editableProducer: string;
  /** Reads the target row and asks its column whether the cell is writable. */
  api: CopyGridApi;
  /** Supplies `metricEditableMapping`, which names a read-only KPI. */
  metaData: ForecastMetaDataType;
}

function hasValue(cell: unknown): cell is number {
  return typeof cell === "number" && Number.isFinite(cell);
}

/**
 * The one producer a scope copies from, or why it has none. Own rows leave the
 * set first: the commonest tick set is the planner's own row beside the one
 * they compare it with.
 */
export function resolveCopySource({
  rows,
  editableProducer
}: CopySourceInput): { producer: string } | { problem: CopySourceProblem } {
  // Frozen: nothing is editable, so nothing is a source (`columnUtils.ts`
  // reads the same undefined as "reject every row").
  if (editableProducer === undefined) return { problem: "noSource" };

  const producers = new Set<string>();
  for (const row of rows) {
    if (row.producer_type === editableProducer) continue;
    producers.add(row.producer_type);
  }
  const [producer] = [...producers];
  if (producer === undefined) return { problem: "noSource" };
  if (producers.size > 1) return { problem: "manyProducers" };
  return { producer };
}

function add<Reason extends string>(
  tally: Tally<Reason>,
  reason: Reason,
  count = 1
) {
  if (count === 0) return;
  tally[reason] = (tally[reason] ?? 0) + count;
}

/** What one source row would write onto its target, and the cells it cannot. */
function planRow(
  row: CopyRow,
  target: CopyRowNode,
  columns: readonly (readonly [string, CopyColumn | null])[],
  editableProducer: string
): Pick<CopyPlan, "changes" | "skipped"> {
  const targetRow = target.data as CopyRow;
  const changes: CopyCellChange[] = [];
  const skipped: Tally<CopyCellSkipReason> = {};
  for (const [colId, column] of columns) {
    const value = row[colId];
    if (!hasValue(value)) {
      add(skipped, "noSourceValue");
      continue;
    }
    if (!column?.isCellEditable(target)) {
      add(skipped, "notEditable");
      continue;
    }
    const oldValue = targetRow[colId];
    changes.push({
      data: {
        metric_name: row.metric_name,
        fiscal_year_name: row.fiscal_year_name,
        producer_type: editableProducer
      },
      colId,
      value,
      oldValue: hasValue(oldValue) ? oldValue : 0
    });
  }
  return { changes, skipped };
}

/** Why a source row with a target wrote nothing: the window, or no values at all. */
function emptyRowReason(skipped: Tally<CopyCellSkipReason>): CopyRowReason {
  return (skipped.notEditable ?? 0) > 0 ? "notEditable" : "noSourceValue";
}

/**
 * The cells a copy writes, and why the rest are left alone.
 *
 * Each source row writes the planner's own row for the same KPI and fiscal
 * year. Own rows in scope are counted, not copied — they are the target. Rows
 * from any other producer are ignored rather than refused —
 * {@link resolveCopySource} is what refuses a mixed scope, and it runs first so
 * the menu can grey out instead of failing on click.
 *
 * A row that writes nothing is reported as a row; only a row that writes
 * something reports the cells it skipped. The toast says each in its own
 * sentence (SPEC.md § Partial copies are normal).
 *
 * Asks the grid, on the Inv 454 view in the ABS plan-by mode only. Under IDX
 * and +/- Var `isCellEditable` answers about the row an edit is redirected to,
 * not the row asked about; the menu holds that gate
 * (`specs/domain/WIP/forecast-algorithms/SPEC.md` § The gate is the grid's own
 * editability rule).
 */
export function buildCopyPlan({
  rows,
  colIds,
  sourceProducer,
  editableProducer,
  api,
  metaData
}: CopyPlanInput): CopyPlan {
  const changes: CopyCellChange[] = [];
  const skipped: Tally<CopyCellSkipReason> = {};
  const rowsNotCopied: Tally<CopyRowReason> = {};

  // Resolved once: a column is the same object for every row under it.
  const columns = colIds.map((colId) => [colId, api.getColumn(colId)] as const);

  for (const row of rows) {
    if (row.producer_type === editableProducer) {
      add(rowsNotCopied, "ownRow");
      continue;
    }
    if (row.producer_type !== sourceProducer) continue;

    // Absent, not just false: the gate reads this mapping as
    // `!mapping[metric]` (`columnUtils.ts` § isCellEditAllowed), and no
    // OPTION_* metric appears in it. It answers "editable", not "calculated" —
    // STORE_COUNT and the OTB metrics are stored, and read-only. The gate
    // refuses every cell of such a KPI, so the row is decided here: its
    // cells are computed by the column and the raw row would read blank.
    if (metaData.metricEditableMapping[row.metric_name] !== true) {
      add(rowsNotCopied, "metricNotEditable");
      continue;
    }

    const target = api.getRowNode(
      createRowId(row.fiscal_year_name, row.metric_name, editableProducer)
    );
    if (!target?.data) {
      add(rowsNotCopied, "noTargetRow");
      continue;
    }

    const rowPlan = planRow(row, target, columns, editableProducer);
    if (rowPlan.changes.length === 0) {
      add(rowsNotCopied, emptyRowReason(rowPlan.skipped));
      continue;
    }
    changes.push(...rowPlan.changes);
    for (const [reason, count] of Object.entries(rowPlan.skipped)) {
      add(skipped, reason as CopyCellSkipReason, count);
    }
  }

  return { changes, skipped, rowsNotCopied };
}
